/**
 * <Input> — bidi-aware text input primitive.
 *
 * DS-08 / UI-SPEC §RTL & Accessibility: email/tel/url inputs are forced
 * `dir="ltr"` regardless of document direction because their content is
 * always Latin-script + punctuation; leaving them at `dir="auto"` in an
 * Arabic shell makes `@`, `/`, `:`, `.` render with confusing visual order
 * (Pitfall H11). All other input types use `dir="auto"` so the browser
 * picks direction from the first strong character the user types — an
 * Arabic name goes RTL, an English name goes LTR, both inside the same
 * Arabic-shell form.
 *
 * Visual states rely on semantic tokens:
 *   - Default:   bg-surface-elevated + border-strong + text-default
 *   - Focus:     accent outline via `focus-ring` @utility
 *   - Disabled:  muted via opacity, pointer-events-none
 *   - Readonly:  lower contrast background (surface-sunken)
 *   - Invalid:   border-danger (driven by `data-invalid="true"`)
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";

/**
 * Input types whose content is always Latin + punctuation. Forced `dir="ltr"`
 * even inside an Arabic shell to avoid bidi reorder of `@`, `.`, `:`, `/`.
 * Extend only after checking UI-SPEC §RTL & Accessibility — the DS-08 list
 * (email/tel/url) is the contract. Keep IBAN/passport UI-level by setting
 * `bidi="ltr"` explicitly on the consumer side.
 */
export const BIDI_FORCED_LTR: ReadonlySet<string> = new Set(["email", "tel", "url"]);

export const inputVariants = cva(
  cn(
    // Shape + layout (logical properties only)
    "block w-full rounded-md border appearance-none",
    // Typography role
    "text-body",
    // Colors — semantic tokens
    "bg-surface-elevated text-default border-strong",
    "placeholder:text-muted",
    // Focus ring
    "focus-visible:focus-ring focus-visible:border-primary",
    // States
    "disabled:opacity-50 disabled:pointer-events-none",
    "read-only:bg-surface-sunken read-only:cursor-default",
    "data-[invalid=true]:border-danger data-[invalid=true]:focus-visible:border-danger",
    "transition-colors duration-fast ease-out",
  ),
  {
    variants: {
      density: {
        comfortable: "h-10 px-md py-sm",
        // Compact subtracts one step from Y-axis padding per UI-SPEC §Spacing.
        compact: "h-9 px-sm py-xs",
      },
      bidi: {
        // auto — browser picks direction from the first strong character.
        // An EMPTY dir="auto" input resolves to ltr in Chrome even on an
        // Arabic page, so an Arabic placeholder sat at the left and, when
        // longer than the box, lost its first words. `plaintext` lays the
        // placeholder out in its own direction: Arabic starts at the right and
        // trims at its end, Latin the reverse.
        auto: "placeholder:[unicode-bidi:plaintext]",
        // ltr — forced regardless of document direction.
        ltr: "",
        // rtl — forced regardless of document direction. For fields that hold
        // one script only (an Arabic name part), where `auto` would leave an
        // empty input pointing the wrong way until the first character lands.
        rtl: "",
      },
    },
    defaultVariants: { density: "comfortable", bidi: "auto" },
  },
);

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "dir"> &
  VariantProps<typeof inputVariants> & {
    /**
     * Force an explicit direction. When omitted, the direction is resolved
     * from the HTML `type` attribute via `BIDI_FORCED_LTR` (email/tel/url →
     * `ltr`; everything else → `auto`).
     */
    bidi?: "auto" | "ltr" | "rtl";
    /**
     * Mark the input as invalid — drives the danger-border state + sets
     * `aria-invalid="true"` so assistive tech announces the error state.
     */
    invalid?: boolean;
    /**
     * Content inside the field at its end, e.g. an AI mark. The slot sits at
     * the end of the field's own text direction, where the field keeps room for
     * it, so it never covers the value — also an LTR value on an RTL page.
     */
    endSlot?: React.ReactNode;
  };

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, density, bidi, type, invalid, endSlot, ...rest },
  ref,
) {
  // DS-08 auto-resolution: if consumer didn't specify bidi, use the type.
  const resolvedBidi: "auto" | "ltr" | "rtl" =
    bidi ?? (type && BIDI_FORCED_LTR.has(type) ? "ltr" : "auto");

  const input = (
    <input
      ref={ref}
      type={type}
      dir={resolvedBidi}
      className={cn(
        inputVariants({ density, bidi: resolvedBidi }),
        endSlot !== undefined && "pe-xl",
        className,
      )}
      aria-invalid={invalid ? true : undefined}
      data-invalid={invalid ? "true" : undefined}
      {...rest}
    />
  );
  if (endSlot === undefined) return input;
  return (
    // The wrapper takes the field's direction, so `end-0` lands where `pe-xl`
    // is: an explicit ltr/rtl field passes it as a plain `dir` (every browser);
    // a dir="auto" field relies on `:dir()`, which follows the typed value
    // (Chrome 120+; older browsers fall back to the page direction).
    <div
      dir={resolvedBidi === "auto" ? undefined : resolvedBidi}
      className="relative [&:has(>input:dir(ltr))]:[direction:ltr] [&:has(>input:dir(rtl))]:[direction:rtl]"
    >
      {input}
      <span className="absolute inset-y-0 end-0 flex items-center pe-xs">{endSlot}</span>
    </div>
  );
});
